import { useState, useEffect, KeyboardEvent, useRef, useCallback } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarDatePicker } from "@/components/calendar-date-picker";
import { DateRange } from "react-day-picker";
import { 
    Search, 
    RotateCcw,
    RefreshCw,
    Timer,
    TimerOff,
    Check,
    Info
} from "lucide-react";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import type { RunStatus, SearchMode, RunSortBy, SortOrder } from '@/api/runs/runs.types';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { NumberInput } from "@/components/ui/number-input";
import { MultiSelect } from "@/components/ui/multi-select";

export interface RunSearchFiltersState {
    query: string;
    searchMode: SearchMode;
    status: RunStatus[];
    flowId: string;
    hasFailedSteps: boolean | undefined;
    dateRange: DateRange;
    minDurationMs: number | undefined;
    maxDurationMs: number | undefined;
    sortBy: RunSortBy;
    sortOrder: SortOrder;
}

interface RunSearchFiltersProps {
    filters: RunSearchFiltersState;
    onFiltersChange: (filters: RunSearchFiltersState) => void;
    onSearch: () => void;
    onReset: (resetFilters: RunSearchFiltersState) => void;
    isLoading?: boolean;
}

// Status options for MultiSelect
const STATUS_OPTIONS = [
    { value: 'SUCCEEDED', label: 'Succeeded' },
    { value: 'FAILED', label: 'Failed' },
    { value: 'RUNNING', label: 'Running' },
    { value: 'PAUSED', label: 'Paused' },
    { value: 'STOPPED', label: 'Stopped' },
    { value: 'TIMEOUT', label: 'Timeout' },
    { value: 'INTERNAL_ERROR', label: 'Internal Error' },
    { value: 'QUOTA_EXCEEDED', label: 'Quota Exceeded' },
];

const AUTO_REFRESH_INTERVALS = [
    { value: 0, label: 'Off' },
    { value: 10, label: '10 seconds' },
    { value: 30, label: '30 seconds' },
    { value: 60, label: '1 minute' },
    { value: 120, label: '2 minutes' },
    { value: 300, label: '5 minutes' },
];

export function RunSearchFilters({ filters, onFiltersChange, onSearch, onReset, isLoading }: RunSearchFiltersProps) {
    const [localFilters, setLocalFilters] = useState<RunSearchFiltersState>(filters);
    const [autoRefreshInterval, setAutoRefreshInterval] = useState(0); // 0 = off
    const [countdown, setCountdown] = useState(0);
    const wasLoadingRef = useRef(false);
    const refocusTargetRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        setLocalFilters(filters);
    }, [filters]);

    // Refocus the input that triggered search when loading completes
    useEffect(() => {
        if (wasLoadingRef.current && !isLoading && refocusTargetRef.current) {
            // Loading just finished, refocus the input that triggered the search
            refocusTargetRef.current.focus();
            refocusTargetRef.current = null;
        }
        wasLoadingRef.current = isLoading ?? false;
    }, [isLoading]);

    // Auto-refresh timer
    useEffect(() => {
        if (autoRefreshInterval === 0 || isLoading) {
            setCountdown(0);
            return;
        }

        // Set initial countdown
        setCountdown(autoRefreshInterval);

        const intervalId = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    // Time to refresh
                    onSearch();
                    return autoRefreshInterval;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(intervalId);
    }, [autoRefreshInterval, isLoading, onSearch]);

    const handleManualRefresh = useCallback(() => {
        // Reset countdown when manually refreshing
        setCountdown(autoRefreshInterval);
        onSearch();
    }, [autoRefreshInterval, onSearch]);

    // Update filter and auto-search for non-text fields
    const handleFilterChangeWithAutoSearch = <K extends keyof RunSearchFiltersState>(
        key: K, 
        value: RunSearchFiltersState[K]
    ) => {
        const newFilters = { ...localFilters, [key]: value };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    // Update filter without auto-search (for text inputs)
    const handleFilterChangeWithoutSearch = <K extends keyof RunSearchFiltersState>(
        key: K, 
        value: RunSearchFiltersState[K]
    ) => {
        const newFilters = { ...localFilters, [key]: value };
        setLocalFilters(newFilters);
        onFiltersChange(newFilters);
    };

    const handleSearchKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            e.stopPropagation();
            // Store which input to refocus after loading completes
            refocusTargetRef.current = e.currentTarget;
            onSearch();
        }
    };

    const handleStatusChange = (values: string[]) => {
        handleFilterChangeWithAutoSearch('status', values as RunStatus[]);
    };

    const handleClearAllFilters = () => {
        const clearedFilters: RunSearchFiltersState = {
            query: '',
            searchMode: 'phrase',
            status: [],
            flowId: '',
            hasFailedSteps: undefined,
            dateRange: { from: undefined, to: undefined },
            minDurationMs: undefined,
            maxDurationMs: undefined,
            sortBy: 'start_time',
            sortOrder: 'desc'
        };
        setLocalFilters(clearedFilters);
        onFiltersChange(clearedFilters);
        onReset(clearedFilters);
    };

    const activeFilterCount = [
        localFilters.query,
        localFilters.flowId,
        ...(localFilters.status || []),
        localFilters.hasFailedSteps !== undefined ? 'hasFailedSteps' : null,
        localFilters.minDurationMs,
        localFilters.maxDurationMs,
        localFilters.dateRange?.from,
        localFilters.dateRange?.to
    ].filter(Boolean).length;

    return (
        <Card className="overflow-hidden">
            <div className="p-4 space-y-3">
                {/* Row 1: Search Query + Search Mode + Search Button */}
                <div className="flex flex-col sm:flex-row gap-2">
                    <div className="flex-1 relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                        <Input
                            placeholder="Search any text in run data (phone, email, IDs, errors, inputs, outputs...)"
                            value={localFilters.query}
                            onChange={(e) => handleFilterChangeWithoutSearch('query', e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            className="pl-9"
                            disabled={isLoading}
                        />
                    </div>
                    <Select 
                        value={localFilters.searchMode} 
                        onValueChange={(value: SearchMode) => handleFilterChangeWithAutoSearch('searchMode', value)}
                    >
                        <SelectTrigger className="w-full sm:w-[220px]">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="w-[220px]">
                            <SelectItem value="phrase">Exact Phrase (in order)</SelectItem>
                            <SelectItem value="match">Contains Words (any)</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button
                        onClick={onSearch}
                        disabled={isLoading}
                        className="gap-2 sm:w-auto"
                    >
                        {isLoading ? (
                            <RefreshCw className="h-4 w-4 animate-spin" />
                        ) : (
                            <Search className="h-4 w-4" />
                        )}
                        <span className="sm:inline">Search</span>
                    </Button>

                    {/* Refresh / Auto-refresh dropdown */}
                    <DropdownMenu>
                        <TooltipProvider delayDuration={300}>
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            disabled={isLoading}
                                            className={cn(
                                                "relative",
                                                autoRefreshInterval > 0 && "border-primary/50"
                                            )}
                                        >
                                            {autoRefreshInterval > 0 ? (
                                                <>
                                                    <Timer className="h-4 w-4 text-primary" />
                                                    <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 text-[10px] font-medium bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                                                        {countdown}
                                                    </span>
                                                </>
                                            ) : (
                                                <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
                                            )}
                                        </Button>
                                    </DropdownMenuTrigger>
                                </TooltipTrigger>
                                <TooltipContent side="bottom">
                                    {autoRefreshInterval > 0 
                                        ? `Auto-refresh in ${countdown}s` 
                                        : "Refresh & Auto-refresh"
                                    }
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                        <DropdownMenuContent align="end" className="w-48">
                            <DropdownMenuItem onClick={handleManualRefresh} disabled={isLoading}>
                                <RefreshCw className="h-4 w-4 mr-2" />
                                Refresh now
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
                                Auto-refresh interval
                            </DropdownMenuLabel>
                            {AUTO_REFRESH_INTERVALS.map((interval) => (
                                <DropdownMenuItem
                                    key={interval.value}
                                    onClick={() => setAutoRefreshInterval(interval.value)}
                                    className="justify-between"
                                >
                                    <span className="flex items-center gap-2">
                                        {interval.value === 0 ? (
                                            <TimerOff className="h-4 w-4" />
                                        ) : (
                                            <Timer className="h-4 w-4" />
                                        )}
                                        {interval.label}
                                    </span>
                                    {autoRefreshInterval === interval.value && (
                                        <Check className="h-4 w-4 text-primary" />
                                    )}
                                </DropdownMenuItem>
                            ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                {/* Row 2: Date Range + Flow ID + Duration + Failed Steps + Status */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
                    {/* Date Range */}
                    <div className="col-span-2 sm:col-span-1 lg:col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Date Range</Label>
                        <CalendarDatePicker
                            date={localFilters.dateRange}
                            onDateSelect={(range) => handleFilterChangeWithAutoSearch('dateRange', range)}
                            numberOfMonths={2}
                            className="w-full"
                        />
                    </div>

                    {/* Flow ID */}
                    <div className="col-span-2 sm:col-span-2 lg:col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Flow ID</Label>
                        <Input
                            placeholder="Filter by flow ID..."
                            value={localFilters.flowId}
                            onChange={(e) => handleFilterChangeWithoutSearch('flowId', e.target.value)}
                            onKeyDown={handleSearchKeyDown}
                            disabled={isLoading}
                            className="h-9"
                        />
                    </div>

                    {/* Duration Range */}
                    <div className="col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Min (sec)</Label>
                        <NumberInput
                            placeholder="0"
                            value={localFilters.minDurationMs ? localFilters.minDurationMs / 1000 : undefined}
                            onValueChange={(value) => {
                                const msValue = value !== undefined ? value * 1000 : undefined;
                                handleFilterChangeWithoutSearch('minDurationMs', msValue);
                            }}
                            min={0}
                            max={localFilters.maxDurationMs ? localFilters.maxDurationMs / 1000 : undefined}
                            stepper={1}
                            disabled={isLoading}
                        />
                    </div>
                    <div className="col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Max (sec)</Label>
                        <NumberInput
                            placeholder="∞"
                            value={localFilters.maxDurationMs ? localFilters.maxDurationMs / 1000 : undefined}
                            onValueChange={(value) => {
                                const msValue = value !== undefined ? value * 1000 : undefined;
                                handleFilterChangeWithoutSearch('maxDurationMs', msValue);
                            }}
                            min={localFilters.minDurationMs ? localFilters.minDurationMs / 1000 : 0}
                            stepper={1}
                            disabled={isLoading}
                        />
                    </div>

                    {/* Failed Steps */}
                    <div className="col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Failed Steps</Label>
                        <Select 
                            value={localFilters.hasFailedSteps === undefined ? 'any' : localFilters.hasFailedSteps ? 'yes' : 'no'}
                            onValueChange={(value) => {
                                const newValue = value === 'any' ? undefined : value === 'yes';
                                handleFilterChangeWithAutoSearch('hasFailedSteps', newValue);
                            }}
                        >
                            <SelectTrigger className="h-9">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="any">Any</SelectItem>
                                <SelectItem value="yes">Has Failed</SelectItem>
                                <SelectItem value="no">No Failures</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Status */}
                    <div className="col-span-2 sm:col-span-2 lg:col-span-1">
                        <Label className="text-xs text-muted-foreground mb-1 block">Status</Label>
                        <MultiSelect
                            options={STATUS_OPTIONS}
                            onValueChange={handleStatusChange}
                            defaultValue={localFilters.status}
                            placeholder="All statuses"
                            maxCount={0}
                            className="h-9"
                        />
                    </div>
                </div>
            </div>

            {/* Footer - Sync info + Reset */}
            <div className="border-t bg-muted/30 px-4 py-2 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <Info className="h-3.5 w-3.5 text-muted-foreground/70 shrink-0" />
                    <p className="text-[11px] text-muted-foreground/70">
                        New runs are synced approximately every minute. Recently executed runs may take a moment to appear.
                    </p>
                </div>
                {activeFilterCount > 0 && (
                    <div className="flex items-center gap-2 shrink-0">
                        <Badge variant="secondary" className="text-xs">
                            {activeFilterCount} active
                        </Badge>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleClearAllFilters}
                            className="h-7 px-3 text-xs gap-1.5"
                        >
                            <RotateCcw className="h-3.5 w-3.5" />
                            Reset Filters
                        </Button>
                    </div>
                )}
            </div>
        </Card>
    );
}
